// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import BaseWidget from './BaseWidget';
import { Icon, ICONS } from '../ui/icons';
import { useLocalization, type Language } from '../../config/localization';
import { useOrganization } from '../../contexts/OrganizationContext';
import Tooltip from '../ui/Tooltip';

interface CollectionItem {
  id: number;
  name: string;
  description?: string;
  is_private: boolean;
  is_default: boolean;
  can_edit: boolean;
}

interface CollectionsSelectorWidgetProps {
  language: Language;
  selectedCollections: number[];
  onCollectionsChange: (collectionIds: number[]) => void;
  skipAutoSelectDefault?: boolean;
  className?: string;
}

export default function CollectionsSelectorWidget({
  language,
  selectedCollections,
  onCollectionsChange,
  skipAutoSelectDefault = false,
  className = ''
}: CollectionsSelectorWidgetProps) {
  const { getToken } = useAuth();
  const { currentOrganization } = useOrganization();
  const t = useLocalization(language);
  
  const [collections, setCollections] = useState<CollectionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Track latest selectedCollections to avoid stale closure in async loadCollections
  const selectedCollectionsRef = useRef(selectedCollections);
  selectedCollectionsRef.current = selectedCollections;
  // Track latest skipAutoSelectDefault to avoid stale closure in async loadCollections
  const skipAutoSelectRef = useRef(skipAutoSelectDefault);
  skipAutoSelectRef.current = skipAutoSelectDefault;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearchTerm(''); // Clear search when closing
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Track current org ID to detect stale responses from previous orgs
  const currentOrgIdRef = useRef(currentOrganization?.id);
  currentOrgIdRef.current = currentOrganization?.id;
  // AbortController ref for cancelling in-flight requests on org change
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load collections when organization changes
  useEffect(() => {
    // Abort any in-flight request from the previous org
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (currentOrganization?.id) {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      loadCollections(currentOrganization.id, controller.signal);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [currentOrganization?.id]);

  const loadCollections = async (orgId: string, signal: AbortSignal) => {
    setLoading(true);
    try {
      const token = await getToken({ template: 'withmail' });
      if (signal.aborted) return;

      const response = await fetch(`/api/organization/collections?organizationId=${orgId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal,
      });

      if (signal.aborted) return;

      if (response.ok) {
        const result = await response.json();
        const collectionsData = result.collections || [];
        console.log('📁 Collections loaded:', collectionsData);

        // Final staleness check: ensure org hasn't changed while we were fetching
        if (currentOrgIdRef.current !== orgId) return;

        setCollections(collectionsData);

        // Auto-select default collection if no collections are selected
        // Use ref to get the latest value, not the stale closure captured when loadCollections started
        // Skip auto-select when collections were explicitly set (e.g., recreate with no collections)
        if (!skipAutoSelectRef.current && selectedCollectionsRef.current.length === 0 && collectionsData.length > 0) {
          const defaultCollection = collectionsData.find((c: CollectionItem) => c.is_default);
          if (defaultCollection) {
            onCollectionsChange([defaultCollection.id]);
          }
        }
      } else {
        console.error('❌ Failed to load collections:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error details:', errorText);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Error loading collections:', error);
      setCollections([]);
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  };

  // Filter collections based on search term
  const filteredCollections = collections.filter(collection =>
    collection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (collection.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle collection toggle
  const handleCollectionToggle = (collectionId: number) => {
    const isSelected = selectedCollections.includes(collectionId);
    
    if (isSelected) {
      // Deselect - but ensure at least one collection remains selected
      const newSelections = selectedCollections.filter(id => id !== collectionId);
      if (newSelections.length > 0) {
        onCollectionsChange(newSelections);
      }
    } else {
      // Select
      onCollectionsChange([...selectedCollections, collectionId]);
    }
  };

  // Get selected collection objects for display
  const getSelectedCollections = () => {
    return collections.filter(collection => selectedCollections.includes(collection.id));
  };

  // Handle search input focus/click to open dropdown
  const handleSearchFocus = () => {
    setIsDropdownOpen(true);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  // Remove collection from selection
  const handleRemoveCollection = (collectionId: number) => {
    const newSelections = selectedCollections.filter(id => id !== collectionId);
    if (newSelections.length > 0) {
      onCollectionsChange(newSelections);
    }
  };

  // Don't show widget if no collections available
  if (!loading && collections.length === 0) {
    return null;
  }

  const selectedCollectionObjects = getSelectedCollections();

  return (
    <BaseWidget
      title={t.widgets?.collectionsSelector || 'Collections'}
      icon={<Icon name={ICONS.COLLECTIONS} size={16} className="text-current" />}
      description={t.widgets?.collectionsSelectorDesc || 'Select collections for your images'}
      language={language}
      className={className}
    >
      <div className="space-y-3" ref={dropdownRef}>
        {/* Search Input */}
        <div className="relative">
          <input
            type="text"
            placeholder={t.widgets?.selectCollections || 'Выберите коллекции...'}
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onClick={handleSearchFocus}
            className="w-full px-3 py-2 pr-16 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2 text-gray-400">
            <Icon 
              name={ICONS.SEARCH} 
              size={16} 
              className="pointer-events-none" 
            />
            <Icon 
              name={isDropdownOpen ? ICONS.CHEVRON_UP : ICONS.CHEVRON_DOWN} 
              size={16} 
              className="transition-transform" 
            />
          </div>
        </div>

        {/* Selected Collections Display (when dropdown is closed) */}
        {!isDropdownOpen && selectedCollectionObjects.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs text-gray-400 font-medium">
              {t.widgets?.selectedCollections || 'Выбранные коллекции'}
            </div>
            <div className="space-y-1">
              {selectedCollectionObjects.map((collection) => (
                <div
                  key={collection.id}
                  className="flex items-center justify-between p-2 bg-purple-900/30 border border-purple-500/50 rounded-lg"
                >
                  <div className="flex items-center space-x-2 min-w-0 flex-1">
                    <span className="text-sm font-medium text-white truncate">
                      {collection.name}
                    </span>
                    {collection.is_default && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-900/50 text-green-300 border border-green-700/50">
                        {t.collections?.default || 'Default'}
                      </span>
                    )}
                    {collection.is_private && (
                      <Icon name={ICONS.SETTINGS} size={12} className="text-yellow-400" />
                    )}
                  </div>
                  {selectedCollections.length > 1 && (
                    <button
                      onClick={() => handleRemoveCollection(collection.id)}
                      className="ml-2 p-1 rounded text-gray-400 hover:text-red-400 hover:bg-red-900/30 transition-colors"
                      title={t.widgets?.removeFromSelection || 'Убрать из выбора'}
                    >
                      <Icon name={ICONS.X} size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Collections Dropdown (when open) */}
        {isDropdownOpen && (
          <div className="relative">
            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-500 border-t-transparent mx-auto mb-2"></div>
                <p className="text-xs text-gray-400">
                  {t.widgets?.loadingCollections || 'Loading collections...'}
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto bg-gray-800 border border-gray-600 rounded-lg p-2">
                {filteredCollections.map((collection) => {
                  const isSelected = selectedCollections.includes(collection.id);
                  
                  return (
                    <label
                      key={collection.id}
                      className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-purple-900/30 border border-purple-500/50' 
                          : 'bg-gray-700/50 border border-transparent hover:bg-gray-600/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCollectionToggle(collection.id)}
                        className="w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-white truncate">
                            {collection.name}
                          </span>
                          {collection.is_default && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-900/50 text-green-300 border border-green-700/50">
                              {t.collections?.default || 'Default'}
                            </span>
                          )}
                          {collection.is_private && (
                            <Icon name={ICONS.SETTINGS} size={12} className="text-yellow-400" />
                          )}
                        </div>
                        {collection.description && (
                          <p className="text-xs text-gray-400 truncate mt-0.5">
                            {collection.description}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
                
                {filteredCollections.length === 0 && searchTerm && (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-400">
                      {t.widgets?.noCollectionsFound || 'No collections found'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Selected count */}
        {selectedCollections.length > 0 && (
          <div className="text-xs text-gray-400 pt-2 border-t border-gray-700">
            {selectedCollections.length === 1 
              ? (t.widgets?.oneCollectionSelected || '1 collection selected')
              : (t.widgets?.collectionsSelected || `${selectedCollections.length} collections selected`).replace('{count}', selectedCollections.length.toString())
            }
          </div>
        )}

        {/* Help tooltip in bottom right corner */}
        <div className="absolute bottom-3 right-3">
          <Tooltip content={t.widgets?.collectionsTooltip || 'Select collections to save generated images. If this widget is not connected, images will be saved to the default collection.'}>
            <div className="w-4 h-4 bg-gray-700/50 border border-gray-600 rounded-full flex items-center justify-center cursor-help hover:bg-gray-600/50 transition-colors duration-200">
              <span className="text-xs text-gray-300 font-medium">?</span>
            </div>
          </Tooltip>
        </div>
      </div>
    </BaseWidget>
  );
}